/**
 * ============================================================================
 *  SEO CONFIG — the single source of truth for metadata & structured data.
 * ============================================================================
 *
 * Everything that used to be hard-coded in `index.html` (title, description,
 * Open Graph / Twitter cards, JSON-LD, local-SEO geo tags) now lives here so a
 * buyer can reskin the template's SEO by editing *one* object — no hunting
 * through markup.
 *
 * This module is deliberately **pure** (no asset imports, no `@` alias). That
 * keeps it importable both from the browser bundle (via the `<Seo>` component)
 * and from the Vite build (to generate `robots.txt` / `sitemap.xml`).
 */

/** Local-SEO geo tags. Set `siteConfig`'s `seo.geo` to `null` to omit them. */
export interface GeoTags {
    /** ISO 3166-2 region code, e.g. "SA-02". */
    region: string;
    /** Human-readable place name, e.g. "Riyadh". */
    placename: string;
    /** `lat;lng`, e.g. "21.4858;39.1925". */
    position: string;
    /** `lat, lng`, e.g. "21.4858, 39.1925". */
    icbm: string;
}

/** Organization contact point rendered into the JSON-LD graph. */
export interface JsonLdContactPoint {
    telephone: string;
    contactType: string;
    /** ISO country code the org serves, e.g. "SA". */
    areaServed: string;
    /** BCP-47 language tags, e.g. ["en", "ar"]. */
    availableLanguage: string[];
}

/** Postal address rendered into the JSON-LD graph. */
export interface JsonLdAddress {
    locality: string;
    /** ISO country code, e.g. "SA". */
    country: string;
}

/** Structured-data (schema.org) inputs used to build the JSON-LD graph. */
export interface OrganizationSchema {
    /** Logo pixel width (for the `ImageObject`). */
    logoWidth: number;
    /** Logo pixel height (for the `ImageObject`). */
    logoHeight: number;
    contactPoint: JsonLdContactPoint;
    address: JsonLdAddress;
}

export interface SeoConfig {
    /** Canonical site origin, **no trailing slash** (e.g. `https://www.example.com`). */
    siteUrl: string;
    /** Default document title (home page / fallback). */
    defaultTitle: string;
    /**
     * Title template for inner routes. The literal `%s` is replaced by the
     * page's own title (e.g. `"%s | Acme"`). If a page passes no title, the
     * `defaultTitle` is used verbatim.
     */
    titleTemplate: string;
    /** Default meta description. */
    description: string;
    /** SEO keywords (rendered as a comma-separated `<meta name="keywords">`). */
    keywords: string[];
    /**
     * Open Graph image. May be an absolute URL or a site-root-relative path
     * (e.g. `/og-image.svg`); relative paths are resolved against `siteUrl`.
     * Swap the file in `public/` and update this value to rebrand the card.
     */
    ogImage: string;
    ogImageAlt: string;
    ogImageWidth: number;
    ogImageHeight: number;
    /** Open Graph locale, e.g. `en_US`. */
    locale: string;
    /** Twitter card type, e.g. `summary_large_image`. */
    twitterCard: string;
    /** Optional local-SEO geo tags. Set to `null` to omit. */
    geo: GeoTags | null;
    /** Structured-data inputs for the JSON-LD `Organization`. */
    organization: OrganizationSchema;
    /**
     * Site-root-relative routes to include in `sitemap.xml`. Add a path here
     * whenever you add a page (e.g. `['/', '/about', '/contact']`).
     */
    routes: string[];
}

export const seoConfig: SeoConfig = {
    siteUrl: 'https://www.BrillianceSolutions.com',
    defaultTitle: 'Brilliance Solutions | Premium Global Meat Import & Distribution',
    titleTemplate: '%s | Brilliance Solutions',
    description:
        'Brilliance Solutions: Premium global meat import & distribution specializing in Bovine meat, Ovine meat, and Offal products. 100% Halal Integrity and Saudi Food and Drug Authority (SFDA) Compliant protein sourcing.',
    keywords: [
        'Bovine Products',
        'Ovine Products',
        'Offal Products',
        'Halal Meat',
        'SFDA Compliant',
        'Global Meat Import & Distribution',
        'Riyadh',
        'Saudi Arabia',
        'Premium Beef',
        'Premium Lamb',
        'Premium Offal',
    ],
    ogImage: '/og-image.svg',
    ogImageAlt: 'Brilliance Solutions Global Sourcing Network Map',
    ogImageWidth: 1200,
    ogImageHeight: 630,
    locale: 'en_US',
    twitterCard: 'summary_large_image',
    geo: {
        region: 'SA-02',
        placename: 'Riyadh',
        position: '21.4858;39.1925',
        icbm: '21.4858, 39.1925',
    },
    organization: {
        logoWidth: 242,
        logoHeight: 270,
        contactPoint: {
            telephone: '+966533878742',
            contactType: 'customer service',
            areaServed: 'SA',
            availableLanguage: ['en', 'ar'],
        },
        address: {
            locality: 'Riyadh',
            country: 'SA',
        },
    },
    // Individual blog post slugs are listed explicitly (rather than importing
    // `src/content/blog.ts` here) to keep this module simple/asset-free per
    // the file-level note above; update this list when adding/removing posts.
    routes: [
        '/',
        '/company',
        '/products',
        '/origins',
        '/quality',
        '/blog',
        '/blog/sourcing-quality-standards',
        '/blog/logistics-cold-chain',
        '/blog/market-outlook-2025',
        '/contact',
    ],
};
